import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Truck, Settings, Wrench, Building2, Users, Award, Phone, Mail, MapPin, ArrowRight, CheckCircle2, Globe, Hammer } from "lucide-react";

// Hero background image
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/8rfedur4_x.jpg";

// Brand logos for carousel - using the uploaded image as single banner
const BRANDS_IMAGE = "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/aapfy8fw_logo";

const services = [
  {
    icon: Wrench,
    title: "Spare Parts Trading",
    company: "MAK Spare Parts",
    description: "Extensive inventory of genuine and quality aftermarket spare parts for all types of heavy machinery. Competitive pricing with same-day delivery available.",
    link: "/companies/mak-spare-parts",
    image: "https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg?auto=compress&cs=tinysrgb&w=600"
  },
  {
    icon: Truck,
    title: "Transport & Equipment Rental",
    company: "HayalogiX",
    description: "Comprehensive transport solutions for heavy equipment and machinery. Modern fleet with experienced operators ensuring safe and timely delivery across the region.",
    link: "/companies/hayalogix",
    image: "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=600"
  },
  {
    icon: Settings,
    title: "Heavy Equipment",
    company: "Bright Perfect",
    description: "Sales, rental, and comprehensive maintenance services for premium construction and industrial machinery. Expert technical support available 24/7.",
    link: "/companies/bright-perfect",
    image: "https://images.pexels.com/photos/1078884/pexels-photo-1078884.jpeg?auto=compress&cs=tinysrgb&w=600"
  },
  {
    icon: Hammer,
    title: "General Contracting Services",
    company: "Hammer Mount",
    description: "Professional general contracting services for construction projects. Expert project management, skilled workforce, and quality workmanship for all your construction needs.",
    link: "/companies/hammer-mount",
    image: "https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=600"
  }
];

const features = [
  {
    icon: Building2,
    title: "State of the Art Facility",
    description: "Modern warehouse facility with climate-controlled storage for parts and equipment. Dedicated workshop with sophisticated equipment."
  },
  {
    icon: Users,
    title: "Experienced Team",
    description: "Highly qualified mechanical and electrical engineers and technicians. Strong sales and administration team with decades of experience."
  },
  {
    icon: Award,
    title: "Quality Assured",
    description: "ISO certified processes and premium quality standards across all operations. All parts and equipment meet international standards."
  }
];

const stats = [
  { value: "15+", label: "Years of Experience" },
  { value: "500+", label: "Projects Completed" },
  { value: "100+", label: "Equipment Fleet" },
  { value: "24/7", label: "Customer Support" }
];

export const Home = () => {
  return (
    <div className="bg-white">
      {/* Hero Section - Static Image */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Heavy Equipment Parts & Solutions"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Hero Content - Bottom Left */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 md:py-32 w-full z-10 flex items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-black/70 backdrop-blur-sm p-6 md:p-8 max-w-xl"
          >
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Heavy Equipment, Parts & Transport Solutions
            </h1>
            <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6">
              Your trusted partner for heavy equipment, machinery spare parts, and transport services in the UAE.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="bg-white/10 border border-white/30 text-white hover:bg-white hover:text-black font-heading font-semibold uppercase tracking-wide px-6 py-3 transition-all inline-flex items-center justify-center"
              >
                About Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#F5B800]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-heading text-3xl md:text-4xl font-bold text-black">{stat.value}</div>
                <div className="text-sm text-black/70 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="intro-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              MAK Heavy Equipment Machinery Spare Parts Trading
            </h2>
            <div className="w-24 h-1 bg-[#F5B800] mx-auto mb-8"></div>
            <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
              <p>
                We offer a comprehensive range of spare parts solutions, including Original Equipment (OE), genuine, and high-quality aftermarket components for a wide variety of heavy machinery. Our portfolio covers equipment such as excavators, dozers, road pavers, wheel loaders, backhoe loaders, crawler loaders, dump trucks, crawler cranes, drilling rigs, surface miners, and other mining equipment.
              </p>
              <p>
                Our product range includes undercarriage parts, cutting edges, Ground Engaging Tools (GET), engine and transmission components, hydraulic parts, mechanical systems, filtration kits, and much more. With a strong inventory, experienced technical team, and wide branch network, we are committed to delivering reliable, cost-effective solutions tailored to your equipment needs.
              </p>
              <p>
                If you have any inquiries about OE or replacement parts, feel free to contact us. Our dedicated specialists are always ready to assist you in finding the right solutions.
              </p>
            </div>
            <div className="mt-8 text-center">
              <a
                href="https://wa.me/971563419000"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold px-8 py-4 transition-all inline-flex items-center gap-2"
              >
                Contact Our Specialists
                <ArrowRight size={20} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Global Delivery Banner */}
      <section className="bg-gray-900 py-16 md:py-20 relative overflow-hidden" data-testid="delivery-banner">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F5B800' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-[#F5B800] rounded-full flex items-center justify-center mx-auto mb-8">
              <Globe size={50} className="text-black" />
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              We Deliver Spare Parts Throughout The<br />
              <span className="text-[#F5B800]">Middle East</span> And Across <span className="text-[#F5B800]">Africa</span>
            </h3>
            <div className="flex items-center justify-center gap-4 mt-8">
              <Link
                to="/companies#contact"
                className="bg-[#F5B800] text-black hover:bg-[#E5AB00] font-bold px-8 py-4 transition-all inline-flex items-center gap-2 text-lg"
              >
                Get Quote
                <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand Partners Carousel */}
      <section className="py-12 md:py-16 bg-white border-y border-gray-100" data-testid="brands-carousel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-8">
            <span className="text-[#D4A000] font-semibold text-sm uppercase tracking-wider">Trusted Brands</span>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-2">
              We Supply Parts For Leading Manufacturers
            </h3>
          </div>
          
          {/* Scrolling Logo Container */}
          <div className="relative overflow-hidden">
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <img src={BRANDS_IMAGE} alt="Brand Partners" className="h-24 md:h-32 w-auto object-contain flex-shrink-0" />
              <div className="w-16 flex-shrink-0" />
              {/* Duplicate for seamless loop */}
              <img src={BRANDS_IMAGE} alt="Brand Partners" className="h-24 md:h-32 w-auto object-contain flex-shrink-0" />
              <div className="w-16 flex-shrink-0" />
              {/* Third copy for longer scroll */}
              <img src={BRANDS_IMAGE} alt="Brand Partners" className="h-24 md:h-32 w-auto object-contain flex-shrink-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Companies & Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Four specialized divisions working together to provide comprehensive solutions for all your heavy equipment needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-[#F5B800] text-black text-xs font-bold px-3 py-1 uppercase tracking-wide">
                      {service.company}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#F5B800]/10 flex items-center justify-center">
                      <service.icon size={22} className="text-[#F5B800]" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-gray-900">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-base leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Link
                    to={service.link}
                    className="text-[#D4A000] hover:text-[#B89000] font-semibold text-sm inline-flex items-center gap-1 transition-colors"
                  >
                    Learn More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Highlight */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#D4A000] font-semibold text-sm uppercase tracking-wider mb-2 block">
                About MAK Group
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Serving Our Customers with Professionalism and Integrity
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                With over 15 years of experience in the heavy equipment industry, MAK Group has established itself as a leading provider of equipment solutions. Our commitment to quality, reliability, and customer satisfaction sets us apart.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#F5B800]" />
                  <span className="text-gray-700">Extensive inventory of genuine spare parts</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#F5B800]" />
                  <span className="text-gray-700">Professional service and repairs</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#F5B800]" />
                  <span className="text-gray-700">Modern fleet for equipment transport</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#F5B800]" />
                  <span className="text-gray-700">Expert consultation and support</span>
                </li>
              </ul>
              <Link
                to="/about"
                data-testid="about-link-btn"
                className="bg-gray-900 text-white hover:bg-gray-800 font-heading font-bold uppercase tracking-wide px-8 py-4 transition-all inline-flex items-center gap-2"
              >
                Learn More About Us
                <ChevronRight size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.pexels.com/photos/1216589/pexels-photo-1216589.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="MAK Group Team"
                className="w-full h-auto shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F5B800] p-6 hidden md:block">
                <div className="font-heading text-4xl font-bold text-black">15+</div>
                <div className="text-black/80 text-sm">Years of Excellence</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-900" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose MAK Group
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-800/50 p-8"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#F5B800] flex items-center justify-center mb-6">
                  <feature.icon size={28} className="text-gray-900" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-base">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Parts Image with Yellow Stripe */}
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_115ec36d-1977-41b2-ba39-5f1407060a77/artifacts/q43jwrf1_footer-parts-costex-2.png"
            alt="Heavy Equipment Parts"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12 md:py-16 bg-[#F5B800]" data-testid="contact-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-black mb-2">
                Need Heavy Equipment Solutions?
              </h2>
              <p className="text-black/70">
                Contact us today for a consultation. We're here to help.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:+971563419000"
                data-testid="cta-phone"
                className="bg-black text-white hover:bg-gray-900 font-heading font-bold uppercase tracking-wide px-8 py-4 transition-all inline-flex items-center justify-center gap-2"
              >
                <Phone size={20} />
                Call Now
              </a>
              <a
                href="mailto:info@makemirates.com"
                data-testid="cta-contact"
                className="bg-white text-black hover:bg-gray-100 font-heading font-bold uppercase tracking-wide px-8 py-4 transition-all inline-flex items-center justify-center gap-2"
              >
                <Mail size={20} />
                Send Enquiry
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
